import React, { useEffect, useState } from 'react';
import { matchPath } from 'react-router-dom';
import { useSyncContentsAssociatedSkillTemplates } from 'components/Skills/queries/skillTemplates';
import { useSyncTaggableToReadinessSkills } from 'components/Skills/queries/skillTags';
import { withSkillsClientWrapper } from 'components/shared/Skills/SkillsClientProvider';
import { withReactQueryWrapper } from 'components/shared/ReactQuery/ReactQueryProvider';
import Error from 'components/InPersonTraining/shared/Error';
import { TEXT_VARIANTS, Text } from 'components/ui/Typography';
import { TYPES, REACT_QUERY_STATUS } from 'constants/config';
import Icon from 'ui/Icon';
import { useToggle, usePermissions, useFeatureFlags } from 'hooks';
import { IconButton, Button } from 'ui';
import EditAssociatedSkills from './EditAssociatedSkills';
import ViewAssociatedSkills from './ViewAssociatedSkills';

export const AssociatedSkillsBase = () => {
  const [editMode, toggleEditMode] = useToggle(false);
  const match = () =>
    matchPath(window.location.pathname, { path: '/lessons/:lessonId' });
  const lessonId = match()?.params?.lessonId;
  const hasSkillsEnabled = useFeatureFlags('readiness-enable-skills-2-0');
  const { isPermitted: isSkillsAdmin } = usePermissions({
    type: 'skill',
    action: 'skills_admin',
  });

  const syncMutation = useSyncContentsAssociatedSkillTemplates({
    contentId: parseInt(lessonId.split('-')[0], 10),
    contentType: TYPES.LESSON,
  });
  const [selectedSkills, setSelectedSkills] = useState([]);

  const syncTagsMutation = useSyncTaggableToReadinessSkills({
    taggableType: TYPES.LESSON,
    taggableId: parseInt(lessonId.split('-')[0], 10),
  });
  const [selectedSkillTags, setSelectedSkillTags] = useState([]);

  useEffect(() => {
    if (syncMutation.status === REACT_QUERY_STATUS.SUCCESS) {
      toggleEditMode();
    }
  }, [syncMutation.status, toggleEditMode]);

  useEffect(() => {
    if (syncTagsMutation.status === REACT_QUERY_STATUS.SUCCESS) {
      toggleEditMode();
    }
  }, [syncTagsMutation.status, toggleEditMode]);

  async function handleSubmit() {
    try {
      if (hasSkillsEnabled) {
        await syncTagsMutation.mutate({
          taggableType: TYPES.LESSON,
          taggableId: parseInt(lessonId.split('-')[0], 10),
          readinessSkillIds: selectedSkillTags.map(sk => sk.id),
        });
      } else {
        await syncMutation.mutate({
          lessonlyContentId: parseInt(lessonId.split('-')[0], 10),
          lessonlyContentType: TYPES.LESSON,
          skillTemplateIds: selectedSkills.map(sk => sk.id),
        });
      }
    } catch (err) {
      console.error(err);
    }
  }

  function handleCancel() {
    toggleEditMode();
    syncMutation.reset();
    syncTagsMutation.reset();
  }

  const EditControls = () => {
    if (editMode) {
      return (
        <span className="float-right">
          <Button onClick={handleCancel} aria-label={t('common.cancel')}>
            {t('common.cancel')}
          </Button>
          <Button customColor onClick={handleSubmit}>
            {t('common.save')}
          </Button>
        </span>
      );
    }

    if (!editMode) {
      return (
        <span className="float-right">
          <IconButton onClick={toggleEditMode} aria-label={t('common.edit')}>
            <Icon name="edit" />
          </IconButton>
        </span>
      );
    }
    return null;
  };

  return (
    <div className="Module EditableModule f-1" style={{ marginRight: 204 }}>
      <div className="Module-heading h-36">
        {isSkillsAdmin && <EditControls />}
        <Text
          component="h3"
          className="SectionHeading"
          variant={TEXT_VARIANTS.BODY}
        >
          {hasSkillsEnabled
            ? t('skills.general.skill_tags.title')
            : t('skills.general.title')}
        </Text>
      </div>
      {editMode && (
        <EditAssociatedSkills
          selectedSkills={selectedSkills}
          setSelectedSkills={setSelectedSkills}
          selectedSkillTags={selectedSkillTags}
          setSelectedSkillTags={setSelectedSkillTags}
        />
      )}
      {(syncMutation.isError || syncTagsMutation.isError) && (
        <Error message={t('common.your_save_failed_please_try_again')} />
      )}
      {!editMode && (
        <ViewAssociatedSkills
          setSelectedSkills={setSelectedSkills}
          setSelectedSkillTags={setSelectedSkillTags}
        />
      )}
    </div>
  );
};

export default withSkillsClientWrapper(
  withReactQueryWrapper(AssociatedSkillsBase),
);
